import Fuse from 'fuse.js';
import {useEffect, useMemo, useRef, useState} from 'react';

import ModelTypeConstants from 'constants/ModelTypeConstants';
import {QueryResultType, useBenchlingQuery} from 'graphql-client/BenchlingQuery';
import {groupSchemasByType} from 'nav/sidepanel/listing/ItemListingUtil';
import {fetchProjectFolderTypeahead, fetchUserTypeahead} from 'omnibox/OmniboxDAO';
import type {ProjectFolderTypeaheadItem, UserTypeaheadItem} from 'omnibox/OmniboxDAO';
import type {TypesFilterSchemasQuery} from 'search/filters/__generated/TypesFilterSchemasQuery';
import type {TypesFilterTagSchemasSSIQuery} from 'search/filters/__generated/TypesFilterTagSchemasSSIQuery';
import {TYPES_FILTER_SCHEMAS_QUERY, TYPES_FILTER_SSI_QUERY} from 'search/filters/TypesAndSchemaFilterForm';
import isModelTypeEnabledForListing from 'util/isModelTypeEnabledForListing';

export interface OmniboxFilter {
  kind: 'type' | 'schema' | 'project' | 'folder' | 'user';
  id: string;
  name: string;
  modelType?: string;
  projectName?: string;
  handle?: string;
  isSelf?: boolean;
  category?: string;
}

// Each entry maps a ModelTypeConstants value to a backend OmniboxObjectType string (id)
// and a display name. The id must match the backend OmniboxObjectType enum value exactly.
// The category field is used for visual grouping in the filter panel.
const OMNIBOX_TYPE_DEFINITIONS: ReadonlyArray<{
  modelType: ModelTypeConstants;
  id: string;
  name: string;
  category: string;
}> = [
  // Organization
  {modelType: ModelTypeConstants.FOLDER, id: 'Folder', name: 'Project or Folder', category: 'Organization'},
  {modelType: ModelTypeConstants.STUDY, id: 'Study', name: 'Study', category: 'Organization'},
  // Notebook
  {modelType: ModelTypeConstants.ENTRY, id: 'Entry', name: 'Entry', category: 'Notebook'},
  {
    modelType: ModelTypeConstants.TEMPLATE_COLLECTION,
    id: 'Template Collection',
    name: 'Template Collection',
    category: 'Notebook',
  },
  {modelType: ModelTypeConstants.PROTOCOL, id: 'Protocol', name: 'Protocol', category: 'Notebook'},
  // Processes
  {modelType: ModelTypeConstants.PROCESS, id: 'Workflow', name: 'Workflow Task Group', category: 'Processes'},
  {
    modelType: ModelTypeConstants.PROCESS_TASK,
    id: 'Workflow Task',
    name: 'Workflow Task',
    category: 'Processes',
  },
  {
    modelType: ModelTypeConstants.PROCESS_OUTPUT,
    id: 'Workflow Output',
    name: 'Workflow Output',
    category: 'Processes',
  },
  {
    modelType: ModelTypeConstants.REQUEST_V2_SUBMISSION,
    id: 'Request',
    name: 'Request',
    category: 'Processes',
  },
  {modelType: ModelTypeConstants.ASSAY_RUN, id: 'Run', name: 'Run', category: 'Processes'},
  {modelType: ModelTypeConstants.BULK_ASSEMBLY, id: 'Assembly', name: 'Assembly', category: 'Processes'},
  // Data
  {modelType: ModelTypeConstants.DATASET, id: 'Dataset', name: 'Dataset', category: 'Data'},
  {modelType: ModelTypeConstants.FILE, id: 'File', name: 'File', category: 'Data'},
  // Analytics
  {modelType: ModelTypeConstants.ANALYSIS, id: 'Analysis', name: 'Analysis', category: 'Analytics'},
  {
    modelType: ModelTypeConstants.ANALYSIS_TEMPLATE,
    id: 'Analysis Template',
    name: 'Analysis Template',
    category: 'Analytics',
  },
  {
    modelType: ModelTypeConstants.ANALYTICS_DASHBOARD,
    id: 'Dashboard',
    name: 'Dashboard',
    category: 'Analytics',
  },
  {
    modelType: ModelTypeConstants.SEQUENCE_ANALYSIS,
    id: 'DNA/RNA Alignment',
    name: 'DNA/RNA Alignment',
    category: 'Analytics',
  },
  {
    modelType: ModelTypeConstants.PROTEIN_ALIGNMENT,
    id: 'AA Alignment',
    name: 'AA Alignment',
    category: 'Analytics',
  },
  // Other
  {modelType: ModelTypeConstants.CONNECTION, id: 'Connection', name: 'Connection', category: 'Other'},
  {modelType: ModelTypeConstants.WORKLIST, id: 'Worklist', name: 'Worklist', category: 'Other'},
  {modelType: ModelTypeConstants.WORKSHEET, id: 'Worksheet', name: 'Worksheet', category: 'Other'},
];

// Category mapping for schemas whose parent types are not in OMNIBOX_TYPE_DEFINITIONS.
// Entity subtypes and inventory types are shown as schemas directly, not as top-level types.
const SCHEMA_CATEGORY_BY_MODEL: Partial<Record<ModelTypeConstants, string>> = {
  [ModelTypeConstants.SEQUENCE]: 'Entities',
  [ModelTypeConstants.RNA_SEQUENCE]: 'Entities',
  [ModelTypeConstants.DNA_OLIGO]: 'Entities',
  [ModelTypeConstants.RNA_OLIGO]: 'Entities',
  [ModelTypeConstants.PROTEIN]: 'Entities',
  [ModelTypeConstants.BASIC_FOLDER_ITEM]: 'Entities',
  [ModelTypeConstants.MIXTURE]: 'Entities',
  [ModelTypeConstants.MOLECULE]: 'Entities',
  [ModelTypeConstants.ANTIBODY]: 'Entities',
  [ModelTypeConstants.EQUIPMENT]: 'Entities',
  [ModelTypeConstants.OLIGO_CONJUGATE]: 'Entities',
  [ModelTypeConstants.OLIGO_DUPLEX]: 'Entities',
  [ModelTypeConstants.CONTAINER]: 'Inventory',
  [ModelTypeConstants.PLATE]: 'Inventory',
  [ModelTypeConstants.BOX]: 'Inventory',
  [ModelTypeConstants.LOCATION]: 'Inventory',
};

// Desired category ordering for the filter panel.
const CATEGORY_ORDER = [
  'Organization',
  'Entities',
  'Notebook',
  'Processes',
  'Data',
  'Inventory',
  'Analytics',
  'Other',
];

function getSearchableTypes(): Array<{
  kind: 'type';
  id: string;
  name: string;
  category: string;
  modelType: ModelTypeConstants;
}> {
  return OMNIBOX_TYPE_DEFINITIONS.filter((def) => isModelTypeEnabledForListing(def.modelType)).map((def) => ({
    kind: 'type' as const,
    id: def.id,
    name: def.name,
    category: def.category,
    modelType: def.modelType,
  }));
}

/**
 * Computes a breadcrumb string for a folder's subtext.
 * - Projects: no subtext
 * - Depth 1 (child of project root): "Project"
 * - Depth 2 (grandchild): "Project / Parent"
 * - Depth 3+ (deeper): "Project / ... / Parent"
 */
function folderBreadcrumb(item: ProjectFolderTypeaheadItem): string | undefined {
  if (item.kind === 'project') {
    return undefined;
  }
  const depth = item.depth ?? 1;
  const projectName = item.projectName;
  if (!projectName) {
    return undefined;
  }
  if (depth <= 1) {
    return projectName;
  }
  const parentName = item.parentFolderName;
  if (!parentName) {
    return projectName;
  }
  if (depth === 2) {
    return `${projectName} / ${parentName}`;
  }
  return `${projectName} / ... / ${parentName}`;
}

function buildFolderFilterItems(items: Array<ProjectFolderTypeaheadItem>): Array<OmniboxFilter> {
  return items.map(
    (item): OmniboxFilter => ({
      kind: item.kind,
      id: item.apiId,
      name: item.name,
      projectName: folderBreadcrumb(item),
    })
  );
}

const FUSE_OPTIONS = {threshold: 0.3, findAllMatches: true, ignoreLocation: false};

export function createFuse<T>(items: Array<T>, keys: Array<string>): Fuse<T> {
  return new Fuse(items, {...FUSE_OPTIONS, keys});
}

export interface TypeaheadData {
  typeaheadItems: Array<OmniboxFilter>;
  projectFolderItems: Array<OmniboxFilter>;
  userItems: Array<OmniboxFilter>;
  fuse: Fuse<OmniboxFilter>;
  projectFuse: Fuse<OmniboxFilter>;
  userFuse: Fuse<OmniboxFilter>;
}

export default function useTypeaheadData(): TypeaheadData {
  const mountedRef = useRef(true);
  useEffect(
    () => () => {
      mountedRef.current = false;
    },
    []
  );

  // Use the same schema queries as search's Type filter for full coverage.
  const schemasResult = useBenchlingQuery<TypesFilterSchemasQuery>(TYPES_FILTER_SCHEMAS_QUERY);
  const ssiResult = useBenchlingQuery<TypesFilterTagSchemasSSIQuery>(TYPES_FILTER_SSI_QUERY);
  const schemasData = schemasResult.type === QueryResultType.SUCCESS ? schemasResult.data : null;
  const ssiData = ssiResult.type === QueryResultType.SUCCESS ? ssiResult.data : undefined;

  const [projectFolderItems, setProjectFolderItems] = useState<Array<OmniboxFilter>>([]);
  useEffect(() => {
    fetchProjectFolderTypeahead()
      .then((items) => {
        if (mountedRef.current) {
          setProjectFolderItems(buildFolderFilterItems(items));
        }
      })
      .catch((e) => {
        // eslint-disable-next-line no-console
        console.error('Failed to fetch project/folder typeahead', e);
      });
  }, []);

  const [userItems, setUserItems] = useState<Array<OmniboxFilter>>([]);
  useEffect(() => {
    fetchUserTypeahead()
      .then((items) => {
        if (mountedRef.current) {
          setUserItems(
            items.map((item: UserTypeaheadItem) => ({
              kind: 'user' as const,
              id: item.apiId,
              name: item.name,
              handle: item.handle,
              isSelf: item.isSelf,
            }))
          );
        }
      })
      .catch((e) => {
        // eslint-disable-next-line no-console
        console.error('Failed to fetch user typeahead', e);
      });
  }, []);

  const searchableTypes = useMemo(() => getSearchableTypes(), []);

  const typeaheadItems = useMemo((): Array<OmniboxFilter> => {
    // Build a lookup from modelType → category for O(1) access in the schema loop.
    const categoryByModel = new Map(searchableTypes.map((t) => [t.modelType, t.category]));

    // Use search's groupSchemasByType to organize schemas by model type.
    // This gives us full coverage of all schema types (workflow task, run, connection, etc.).
    const schemasByParent = new Map<string, Array<OmniboxFilter>>();
    if (schemasData) {
      const grouped = groupSchemasByType(schemasData, ssiData);
      const starredIds = new Set(schemasData.starredTagSchemas.map((s) => s.id));

      for (const [modelType, schemas] of Object.entries(grouped)) {
        // groupSchemasByType maps both PROCESS and PROCESS_TASK to the same schemas;
        // skip PROCESS to avoid duplicates (schemas appear under Workflow Task instead).
        if (modelType === ModelTypeConstants.PROCESS) {
          continue;
        }
        const activeSchemas = schemas.filter((s) => !('archiveRecord' in s && s.archiveRecord));
        if (activeSchemas.length === 0) {
          continue;
        }
        const category =
          categoryByModel.get(modelType as ModelTypeConstants) ??
          SCHEMA_CATEGORY_BY_MODEL[modelType as ModelTypeConstants];
        if (!category) {
          continue;
        }
        // Sort starred schemas first.
        const starred = activeSchemas.filter((s) => starredIds.has(s.id));
        const rest = activeSchemas.filter((s) => !starredIds.has(s.id));
        schemasByParent.set(
          modelType,
          [...starred, ...rest].map(
            (s): OmniboxFilter => ({
              kind: 'schema',
              id: s.id,
              name: s.name,
              modelType: modelType as ModelTypeConstants,
              category,
            })
          )
        );
      }
    }

    // Group types by category for ordered iteration.
    const typesByCategory = new Map<string, Array<(typeof searchableTypes)[0]>>();
    for (const type of searchableTypes) {
      const arr = typesByCategory.get(type.category) ?? [];
      arr.push(type);
      typesByCategory.set(type.category, arr);
    }

    // Build items in category order: types with interleaved child schemas,
    // then orphan schemas (entity/inventory) in their correct position.
    const items: Array<OmniboxFilter> = [];
    const usedParents = new Set<string>();

    for (const category of CATEGORY_ORDER) {
      const typesInCategory = typesByCategory.get(category) ?? [];
      for (const type of typesInCategory) {
        items.push(type);
        const childSchemas = schemasByParent.get(type.modelType);
        if (childSchemas) {
          items.push(...childSchemas);
          usedParents.add(type.modelType);
        }
      }

      // Orphan schemas for this category (entity/inventory schemas whose parent types
      // are not in OMNIBOX_TYPE_DEFINITIONS).
      for (const [parentModel, schemas] of schemasByParent) {
        if (usedParents.has(parentModel)) {
          continue;
        }
        const cat = schemas[0]?.category;
        if (cat === category) {
          items.push(...schemas);
          usedParents.add(parentModel);
        }
      }
    }

    // Append any remaining orphans that didn't match a known category.
    for (const [parentModel, schemas] of schemasByParent) {
      if (!usedParents.has(parentModel)) {
        items.push(...schemas);
      }
    }

    return items;
  }, [schemasData, ssiData, searchableTypes]);

  const fuse = useMemo(() => createFuse(typeaheadItems, ['name']), [typeaheadItems]);

  const projectFuse = useMemo(
    () => createFuse(projectFolderItems, ['name', 'projectName']),
    [projectFolderItems]
  );

  const userFuse = useMemo(() => createFuse(userItems, ['name', 'handle']), [userItems]);

  return {
    typeaheadItems,
    projectFolderItems,
    userItems,
    fuse,
    projectFuse,
    userFuse,
  };
}
